import { useState, useEffect } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import MFAEnrollment from './MFAEnrollment'

function MFASettings({ onMFAChange }) {
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showEnrollment, setShowEnrollment] = useState(false)
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [factorId, setFactorId] = useState(null)
  const [disabling, setDisabling] = useState(false)
  const [error, setError] = useState(null)

  // Check current MFA status
  useEffect(() => {
    const checkMFAStatus = async () => {
      try {
        const { data, error } = await supabase.auth.mfa.listFactors()
        if (error) throw error

        const verifiedFactor = data.totp?.find(f => f.status === 'verified')
        setMfaEnabled(!!verifiedFactor)
        setFactorId(verifiedFactor?.id || null)
      } catch (err) {
        console.error('Error checking MFA status:', err)
      } finally {
        setLoading(false)
      }
    }

    checkMFAStatus()
  }, [])

  const handleEnrollmentComplete = () => {
    setShowEnrollment(false)
    setMfaEnabled(true)
    onMFAChange?.()

    // Refresh factor ID
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const verifiedFactor = data?.totp?.find(f => f.status === 'verified')
      setFactorId(verifiedFactor?.id || null)
    })
  }

  const handleDisableMFA = async () => {
    if (!factorId) return

    setDisabling(true)
    setError(null)

    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId })
      if (error) throw error

      setMfaEnabled(false)
      setFactorId(null)
      setShowDisableConfirm(false)
      onMFAChange?.()
    } catch (err) {
      setError(err.message)
    } finally {
      setDisabling(false)
    }
  }

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading MFA status...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              mfaEnabled
                ? 'bg-green-100 dark:bg-green-900/40'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              <svg
                className={`w-5 h-5 ${mfaEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {mfaEnabled
                  ? 'Your account is protected with two-factor authentication.'
                  : 'Add an extra layer of security to your account by requiring a verification code in addition to your password.'
                }
              </p>
              {mfaEnabled && (
                <div className="flex items-center mt-2">
                  <svg className="w-4 h-4 text-green-500 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium">Enabled</span>
                </div>
              )}
            </div>
          </div>

          <div>
            {mfaEnabled ? (
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
              >
                Disable
              </button>
            ) : (
              <button
                onClick={() => setShowEnrollment(true)}
                className="btn-primary text-sm"
              >
                Enable MFA
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Enrollment Modal */}
      {showEnrollment && (
        <MFAEnrollment
          onComplete={handleEnrollmentComplete}
          onCancel={() => setShowEnrollment(false)}
        />
      )}

      {/* Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mr-3">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Disable Two-Factor Authentication?
              </h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Disabling two-factor authentication will make your account less secure.
              You will only need your password to sign in.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisableConfirm(false)}
                className="btn-secondary"
                disabled={disabling}
              >
                Keep Enabled
              </button>
              <button
                onClick={handleDisableMFA}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                disabled={disabling}
              >
                {disabling ? 'Disabling...' : 'Disable MFA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default MFASettings
